/**
 * useBankSync — the queue tab's bank-drift remedies, both horizons:
 * `syncNow(layers)` pulls every affected layer from the campaign's own tracker
 * SEQUENTIALLY (paths first — landers/offers hang off paths), and
 * `keepSynced(layers)` arms the standing Auto-pull for those resources so the
 * bank follows another environment's rotations WITHOUT a human pressing
 * buttons. Pulls are read-only against the tracker (they only write the local
 * bank, mode 'skip' keeps curated rows untouched).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AutoPullResource, BankAutoPullConfig, FlowLayer } from '@trafficpulse/shared';

import api from '@/services/api';
import { getApiErrorMessage } from '@/lib/api-error';
import { useToast } from '@/providers/ToastProvider';
import { bankSyncTarget } from '@/features/flow-optimizer/tabs/queue/sync/bank-sync-target';
import { pullBankLayers } from '@/features/flow-optimizer/tabs/queue/sync/pull-bank-layers';
import { queueProviderOf, type QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { queueKeys } from '../../hooks/useFlowOptimizerQueue';

const AUTO_PULL_INTERVAL_MIN = 30;

export function useBankSync(provider: QueueProvider, campaignId: string) {
  const target = bankSyncTarget(provider);
  const qc = useQueryClient();
  const toast = useToast();
  const [syncing, setSyncing] = useState<AutoPullResource | null>(null);
  const [arming, setArming] = useState(false);

  // Shares the Banks page's auto-pull cache (same key), so both stay in step.
  const autoPull = useQuery({
    queryKey: target.key,
    queryFn: () =>
      api
        .get<{ data: BankAutoPullConfig[] }>(target.url)
        .then((r) => r.data.data),
    staleTime: 30_000,
    enabled: queueProviderOf(provider) !== null,
  });

  const syncNow = async (layers: FlowLayer[]) => {
    if (!queueProviderOf(provider)) return;
    try {
      await pullBankLayers(provider, layers, setSyncing);
      // Pulls create ROWS; the pools are junction-driven. Turn flow truth
      // into links so the pulled rows actually become manageable.
      const { data } = await api.post<{
        data?: { linkedLanders?: number; linkedOffers?: number };
      }>(`/${provider}/flow-optimizer/${campaignId}/queue/link-live-items`, {});
      const linked =
        (data?.data?.linkedLanders ?? 0) + (data?.data?.linkedOffers ?? 0);
      toast.success(
        'Bank synced',
        `Pulled ${layers.join(' + ')} from ${provider}` +
          (linked ? ` · ${linked} link${linked === 1 ? '' : 's'} created` : ''),
      );
    } catch (e) {
      toast.error('Sync failed', getApiErrorMessage(e));
    } finally {
      setSyncing(null);
      void qc.invalidateQueries({ queryKey: queueKeys.queue(provider, campaignId) });
    }
  };

  const keepSynced = async (layers: FlowLayer[]) => {
    if (!queueProviderOf(provider)) return;
    setArming(true);
    try {
      for (const layer of target.resources(layers)) {
        const current = autoPull.data?.find((c) => c.resource === layer);
        await api.put(`${target.url}/${layer}`, {
          enabled: true,
          intervalMin: current?.intervalMin ?? AUTO_PULL_INTERVAL_MIN,
        });
      }
      toast.success(
        'Auto-pull armed',
        `${layers.join(' + ')} now sync automatically`,
      );
      void qc.invalidateQueries({ queryKey: target.key });
    } catch (e) {
      toast.error('Could not arm auto-pull', getApiErrorMessage(e));
    } finally {
      setArming(false);
    }
  };

  /** The affected resources NOT yet covered by an armed auto-pull. */
  const unarmed = (layers: FlowLayer[]): FlowLayer[] =>
    layers.filter(
      (l) => target.resources([l]).some((resource) =>
        !autoPull.data?.some((c) => c.resource === resource && c.enabled)),
    );

  return { syncing, syncNow, arming, keepSynced, unarmed };
}
